// Merchant quote builder. Opens a quote_pending request, pre-fills the
// requested items as priced lines (editable), lets the merchant set
// delivery + ETA + notes, previews the customer's USD total (FX + platform
// fee mirror the backend), and submits. If the request is already quoted,
// shows the submitted quote read-only.

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Plus, Trash2, Send, MapPin, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, StatusPill } from '../../components/shared/primitives';
import { Button } from '../../components/stripe-ui/Button';
import { MERCHANT_NAV } from '../_navs';
import { requests, catalog, type SubmitQuoteInput, type ServiceRequestWithItems } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

// Mirror of the backend QuoteController for an accurate preview. FX + fee are
// admin-configurable, so both are fetched live (fallbacks to sane defaults).
const FX_FALLBACK = 132.4;

interface Line { description: string; quantity: number; unit_gourdes: number }

function usd(minor: number) { return `$${(minor / 100).toFixed(2)}`; }

export function MerchantQuoteBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['merchant', 'request', requestId],
    queryFn:  () => requests.get(requestId),
    enabled:  !isNaN(requestId),
  });
  const r = q.data;

  return (
    <PortalShell
      nav={MERCHANT_NAV}
      activeId="inbox"
      title={r ? `Quote REQ-${String(r.id).padStart(4, '0')}` : 'Build quote'}
      subtitle={r ? `${r.recipient?.full_name ?? ''} · ${r.city?.name ?? ''}` : undefined}
    >
      <div className="space-y-5">
        <Link to="/merchant/quote-requests" className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to inbox
        </Link>

        {q.isLoading && <Card><div className="text-center py-12 text-[13px] text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div></Card>}
        {q.error && <Card><p className="text-[13px] text-rose-700">{(q.error as Error).message}</p></Card>}

        {r && (
          <>
            <Card>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <StatusPill status={r.status} />
                  <h2 className="mt-2 text-[18px] font-semibold tracking-tight text-slate-900">{r.title || `Request for ${r.recipient?.full_name}`}</h2>
                  {r.notes && <p className="mt-1 text-[12.5px] text-slate-600">Note from customer: “{r.notes}”</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Meta icon={UserIcon} label="Recipient" value={r.recipient?.full_name ?? '—'} />
                  <Meta icon={MapPin} label="City" value={r.city?.name ?? '—'} />
                </div>
              </div>
            </Card>

            {r.status === 'quote_pending'
              ? <QuoteForm request={r} onSubmitted={() => { qc.invalidateQueries({ queryKey: ['merchant', 'incoming'] }); navigate('/merchant/quote-requests'); }} />
              : <ExistingQuote request={r} />}
          </>
        )}
      </div>
    </PortalShell>
  );
}

function QuoteForm({ request, onSubmitted }: { request: ServiceRequestWithItems; onSubmitted: () => void }) {
  const [lines, setLines] = useState<Line[]>(() =>
    (request.items ?? []).map((it) => ({
      description: it.name_snapshot,
      quantity: Number(it.quantity),
      unit_gourdes: Math.round(it.unit_price_htg_minor / 100),
    })),
  );
  const [deliveryGourdes, setDeliveryGourdes] = useState(0);
  const [etaHours, setEtaHours] = useState<number | ''>(4);
  const [expiryHours, setExpiryHours] = useState(48);
  const [notes, setNotes] = useState('');

  const fxQ = useQuery({ queryKey: ['catalog', 'fx'], queryFn: catalog.fx });
  const fx = fxQ.data?.htg_per_usd ?? FX_FALLBACK;
  const feeQ = useQuery({ queryKey: ['catalog', 'fee', request.category_id, request.city_id], queryFn: () => catalog.fee(request.category_id, request.city_id) });
  const fee = feeQ.data;

  const setLine = (i: number, patch: Partial<Line>) => setLines((p) => p.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i: number) => setLines((p) => p.filter((_, idx) => idx !== i));
  const addLine = () => setLines((p) => [...p, { description: '', quantity: 1, unit_gourdes: 0 }]);

  const { subtotalMinor, deliveryMinor, goodsDeliveryUsdMinor, feeUsdMinor, totalUsdMinor } = useMemo(() => {
    const subtotalMinor = lines.reduce((a, l) => a + Math.round(l.unit_gourdes * 100 * l.quantity), 0);
    const deliveryMinor = Math.round(deliveryGourdes * 100);
    const goodsDeliveryUsdMinor = Math.round((subtotalMinor + deliveryMinor) / fx);
    const pctBps = fee?.fee_pct_bps ?? 1200;
    const flat = fee?.fee_flat_usd_minor ?? 0;
    const minFee = fee?.min_fee_usd_minor ?? 150;
    let feeUsdMinor = Math.round(goodsDeliveryUsdMinor * pctBps / 10000) + flat;
    feeUsdMinor = Math.max(feeUsdMinor, minFee);
    if (fee?.max_fee_usd_minor != null) feeUsdMinor = Math.min(feeUsdMinor, fee.max_fee_usd_minor);
    return { subtotalMinor, deliveryMinor, goodsDeliveryUsdMinor, feeUsdMinor, totalUsdMinor: goodsDeliveryUsdMinor + feeUsdMinor };
  }, [lines, deliveryGourdes, fx, fee]);

  const submit = useMutation({
    mutationFn: () => {
      const valid = lines.filter((l) => l.description.trim() && l.quantity > 0);
      if (valid.length === 0) throw new Error('Add at least one priced item.');
      const input: SubmitQuoteInput = {
        items: valid.map((l) => ({ description: l.description.trim(), quantity: l.quantity, unit_htg_minor: Math.round(l.unit_gourdes * 100) })),
        delivery_htg_minor: deliveryMinor,
        est_fulfillment_hours: etaHours === '' ? null : Number(etaHours),
        expires_in_hours: expiryHours,
        merchant_notes: notes.trim() || null,
      };
      return requests.submitQuote(request.id, input);
    },
    onSuccess: () => { toast.success('Quote sent to the customer'); onSubmitted(); },
    onError: (e: Error) => toast.error(e.message || 'Could not submit quote'),
  });

  return (
    <>
      <Card padding="p-0">
        <div className="p-5 border-b border-slate-100"><SectionHeader title="Price the items" subtitle="Adjust prices to today's stock. Remove anything you can't fill; add lines as needed." /></div>
        <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
          <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
            <tr>
              <th className="text-left py-2.5 px-5 font-medium">Item</th>
              <th className="text-right py-2.5 px-3 font-medium w-20">Qty</th>
              <th className="text-right py-2.5 px-3 font-medium w-32">Unit (HTG)</th>
              <th className="text-right py-2.5 px-3 font-medium w-32">Line (HTG)</th>
              <th className="py-2.5 px-5 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lines.map((l, i) => (
              <tr key={i}>
                <td className="py-2 px-5">
                  <input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Item name" className="w-full px-2 py-1.5 text-[13px] border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400" />
                </td>
                <td className="py-2 px-3"><input type="number" min={0} step="0.001" value={l.quantity} onChange={(e) => setLine(i, { quantity: Number(e.target.value) })} className="w-full px-2 py-1.5 text-[13px] text-right font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" /></td>
                <td className="py-2 px-3"><input type="number" min={0} step="1" value={l.unit_gourdes} onChange={(e) => setLine(i, { unit_gourdes: Number(e.target.value) })} className="w-full px-2 py-1.5 text-[13px] text-right font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" /></td>
                <td className="py-2 px-3 text-right font-mono text-slate-900">{(l.unit_gourdes * l.quantity).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td className="py-2 px-5 text-right"><button onClick={() => removeLine(i)} className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-3 border-t border-slate-100">
          <button onClick={addLine} className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 hover:underline"><Plus className="w-3.5 h-3.5" /> Add line</button>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-5">
        <Card className="md:col-span-2 space-y-3">
          <SectionHeader title="Delivery & terms" />
          <div className="grid grid-cols-3 gap-3">
            <Labeled label="Delivery fee (HTG)"><input type="number" min={0} value={deliveryGourdes} onChange={(e) => setDeliveryGourdes(Number(e.target.value))} className={inp} /></Labeled>
            <Labeled label="ETA (hours)"><input type="number" min={1} value={etaHours} onChange={(e) => setEtaHours(e.target.value === '' ? '' : Number(e.target.value))} className={inp} /></Labeled>
            <Labeled label="Quote valid (hours)"><input type="number" min={1} max={336} value={expiryHours} onChange={(e) => setExpiryHours(Number(e.target.value))} className={inp} /></Labeled>
          </div>
          <Labeled label="Note to customer (optional)"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inp} placeholder="Substitutions, timing, etc." /></Labeled>
        </Card>

        <Card className="space-y-2 self-start">
          <SectionHeader title="Customer pays" />
          <Row label="Goods subtotal" value={formatHtgMinor(subtotalMinor)} />
          <Row label="Delivery" value={formatHtgMinor(deliveryMinor)} />
          <Row label="Goods + delivery" value={`≈ ${usd(goodsDeliveryUsdMinor)}`} />
          <Row label={`Platform fee (${((fee?.fee_pct_bps ?? 1200) / 100).toFixed(1)}%)`} value={usd(feeUsdMinor)} />
          <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
            <span className="text-[12px] font-semibold text-slate-700">Total (USD)</span>
            <span className="text-[18px] font-mono font-semibold text-slate-900">{usd(totalUsdMinor)}</span>
          </div>
          <Button fullWidth size="lg" className="mt-2" onClick={() => submit.mutate()} loading={submit.isPending} disabled={submit.isPending} leadingIcon={<Send className="w-3.5 h-3.5" />}>
            Send quote
          </Button>
          <p className="text-[10.5px] text-slate-400 leading-relaxed">FX ≈ {fx.toFixed(2)} HTG/USD, snapshotted onto the quote. The customer approves before any charge.</p>
        </Card>
      </div>
    </>
  );
}

function ExistingQuote({ request }: { request: ServiceRequestWithItems }) {
  const quote = request.quotes?.[0];
  if (!quote) return <Card><p className="text-[13px] text-slate-500">This request is <StatusPill status={request.status} /> — no quote to show.</p></Card>;
  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <SectionHeader title="Your quote" subtitle={`Sent ${new Date(quote.created_at).toLocaleString()}`} />
        <StatusPill status={quote.status} />
      </div>
      <div className="space-y-1.5 max-w-sm">
        <Row label="Goods subtotal" value={formatHtgMinor(quote.subtotal_htg_minor)} />
        <Row label="Delivery" value={formatHtgMinor(quote.delivery_htg_minor)} />
        <Row label="Platform fee" value={usd(quote.platform_fee_usd_minor)} />
        <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
          <span className="text-[12px] font-semibold text-slate-700">Customer total (USD)</span>
          <span className="text-[16px] font-mono font-semibold text-slate-900">{usd(quote.total_customer_usd_minor)}</span>
        </div>
      </div>
      {quote.merchant_notes && <p className="mt-3 text-[12px] text-slate-600">Note: {quote.merchant_notes}</p>}
    </Card>
  );
}

const inp = 'w-full px-2.5 py-2 text-[13px] bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400';

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</span><div className="mt-1">{children}</div></label>;
}
function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between text-[12.5px]"><span className="text-slate-500">{label}</span><span className="font-mono text-slate-900">{value}</span></div>;
}
function Meta({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return <div className="border border-slate-100 rounded-lg p-2.5"><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p><p className="mt-0.5 text-[12.5px] font-semibold text-slate-900 truncate">{value}</p></div>;
}
